import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Space,
  Button,
  Input,
  Select,
  DatePicker,
  Typography,
  Tag,
  Modal,
  message,
  Row,
  Col,
  Tooltip,
  Badge
} from 'antd';
import {
  CheckOutlined,
  SwapOutlined,
  SearchOutlined,
  DownloadOutlined,
  EyeOutlined,
  FilterOutlined,
  ClearOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import InvoiceModal from './InvoiceModal';
import ReceiptModal from './ReceiptModal';
import RefundModal from './RefundModal';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const formatNumber = (number, locale) => {
  if (isNaN(number)) return '0.00';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number);
};

const getStatusColor = (status) => {
  switch (status) {
    case 'confirmed':
      return 'success';
    case 'refund':
      return 'error';
    case 'unconfirmed':
    default:
      return 'warning';
  }
};

const getStatusText = (status, t) => {
  switch (status) {
    case 'confirmed':
      return t('confirmed');
    case 'refund':
      return t('refunded');
    case 'unconfirmed':
    default:
      return t('pending');
  }
};

const Accounting = () => {
  const { t, i18n } = useTranslation();
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  
  // Filter states
  const [filters, setFilters] = useState({
    dateRange: [
      dayjs().startOf('month'), // First day of current month
      dayjs().endOf('month')    // Last day of current month
    ],
    paymentMethod: '',
    studentName: '',
    status: ''
  });

  // Pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
  });

  // Modal states
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  const fetchRegistrations = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/registrations`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const registrationList = response.data;
      const ids = registrationList.map(reg => reg.id);

      // Batch status check
      const statusResponse = await axios.post(
        `${API_URL}/registrations/confirm-status`,
        { ids },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const statusMap = statusResponse.data;
      const data = registrationList.map(reg => ({
        ...reg,
        key: reg.id,
        confirmation_status: statusMap[reg.id] || 'unconfirmed',
      }));

      setRegistrations(data);
      
      // Extract unique payment methods
      const methods = [...new Set(data.map(reg => reg.payment_method))];
      setPaymentMethods(methods);
      
      message.success(t('dataLoadedSuccessfully'));
    } catch (error) {
      console.error('Error fetching registrations:', error);
      message.error(t('errorLoadingData'));
    } finally {
      setLoading(false);
    }
  }, [API_URL, t]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  // Apply filters
  useEffect(() => {
    let filtered = [...registrations];

    // Date range filter
    if (filters.dateRange && filters.dateRange.length === 2) {
      const [startDate, endDate] = filters.dateRange;
      filtered = filtered.filter(reg => {
        const regDate = dayjs(reg.date);
        return regDate.isAfter(startDate.subtract(1, 'day')) && 
               regDate.isBefore(endDate.add(1, 'day'));
      });
    }

    // Payment method filter
    if (filters.paymentMethod) {
      filtered = filtered.filter(reg => reg.payment_method === filters.paymentMethod);
    }

    // Student name filter
    if (filters.studentName) {
      filtered = filtered.filter(reg =>
        reg.student_name.toLowerCase().includes(filters.studentName.toLowerCase())
      );
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(reg => reg.confirmation_status === filters.status);
    }

    setFilteredRegistrations(filtered);
    setPagination(prev => ({ ...prev, current: 1, total: filtered.length }));
  }, [registrations, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      dateRange: [dayjs().startOf('month'), dayjs().endOf('month')],
      paymentMethod: '',
      studentName: '',
      status: ''
    });
  };

  const handleConfirmClick = async (registrationId) => {
    Modal.confirm({
      title: t('confirm_registration'),
      content: t('Are you sure you want to confirm this registration?'),
      okText: t('yes'),
      cancelText: t('no'),
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${API_URL}/registrations/confirm`, 
            { registrationId }, 
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          message.success(t('paymentConfirmed'));
          fetchRegistrations();
        } catch (error) {
          console.error('Error confirming registration:', error);
          message.error(t('errorConfirmingPayment'));
        }
      }
    });
  };

  const calculateTotals = () => {
    return filteredRegistrations.reduce(
      (totals, registration) => {
        totals.price += parseFloat(registration.price) || 0;
        totals.inventory_price += parseFloat(registration.inventory_price) || 0;
        totals.discount += parseFloat(registration.discount) || 0;
        totals.vat += parseFloat(registration.vat) || 0;
        totals.finalPrice += parseFloat(registration.final_price) || 0;
        return totals;
      },
      { price: 0, inventory_price: 0, discount: 0, vat: 0, finalPrice: 0 }
    );
  };

  const totals = calculateTotals();

  // CSV data preparation
const csvData = [
  // Each registration becomes a flat row with clean data
  ...filteredRegistrations.map(reg => ({
    invoiceno: reg.invoiceno || '',
    date: dayjs(reg.date).format('YYYY-MM-DD'),
    student_name: reg.student_name || '',
    class_code: reg.class_code || '',
    price: reg.price || 0,
    discount: reg.discount || 0,
    vat: reg.vat || 0,
    final_price: reg.final_price || 0,
    payment_method: reg.payment_method || '',
    memo: reg.memo || '',
    confirmation_status: getStatusText(reg.confirmation_status, t),
    admin_id: reg.admin_id || ''
  })),

  // Append totals row at the end
  {
    invoiceno: 'TOTAL',
    date: '',
    student_name: '',
    class_code: '',
    price: totals.price || 0,
    discount: totals.discount || 0,
    vat: totals.vat || 0,
    final_price: totals.finalPrice || 0,
    payment_method: '',
    memo: '',
    confirmation_status: '',
    admin_id: ''
  }
];


  const columns = [
    {
      title: t('invoiceNo'),
      dataIndex: 'invoiceno',
      key: 'invoiceno',
      width: 120,
      sorter: (a, b) => a.invoiceno.localeCompare(b.invoiceno),
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => {
            setSelectedRegistration(record);
            setShowInvoiceModal(true);
          }}
          style={{ 
            color: record.confirmation_status === 'refund' ? '#ff4d4f' : '#1890ff',
            padding: 0,
            whiteSpace: 'nowrap'
          }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: t('date'),
      dataIndex: 'date',
      key: 'date',
      width: 120,
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
      render: (date) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {dayjs(date).format('DD MMM YYYY')}
        </span>
      ),
    },
    {
      title: t('student'),
      dataIndex: 'student_name',
      key: 'student_name',
      width: 180,
      sorter: (a, b) => a.student_name.localeCompare(b.student_name),
      render: (text) => (
        <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
      ),
    },
    {
      title: t('course'),
      dataIndex: 'class_code',
      key: 'class_code',
      width: 120,
      render: (text) => (
        <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
      ),
    },
    {
      title: t('price'),
      dataIndex: 'price',
      key: 'price',
      width: 100,
      align: 'right',
      sorter: (a, b) => parseFloat(a.price) - parseFloat(b.price),
      render: (price) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {formatNumber(price, i18n.language)}
        </span>
      ),
    },
    {
      title: t('inventory_price'),
      dataIndex: 'inventory_price',
      key: 'inventory_price',
      width: 120,
      align: 'right',
      render: (price) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {formatNumber(price, i18n.language)}
        </span>
      ),
    },
    {
      title: t('discount'),
      dataIndex: 'discount',
      key: 'discount',
      width: 100,
      align: 'right',
      render: (discount) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {formatNumber(discount, i18n.language)}
        </span>
      ),
    },
    {
      title: t('vat'),
      dataIndex: 'vat',
      key: 'vat',
      width: 100,
      align: 'right',
      render: (vat) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {formatNumber(vat, i18n.language)}
        </span>
      ),
    },
    {
      title: t('total'),
      dataIndex: 'final_price',
      key: 'final_price',
      width: 120,
      align: 'right',
      sorter: (a, b) => parseFloat(a.final_price) - parseFloat(b.final_price),
      render: (price) => (
        <strong style={{ color: '#1890ff', whiteSpace: 'nowrap' }}>
          {formatNumber(price, i18n.language)}
        </strong>
      ),
    },
    {
      title: t('payment'),
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 120,
      render: (method) => (
        <Tag color="blue" style={{ whiteSpace: 'nowrap' }}>
          {method}
        </Tag>
      ),
    },
    {
      title: t('status'),
      dataIndex: 'confirmation_status',
      key: 'confirmation_status',
      width: 110,
      render: (status) => (
        <Badge 
          status={getStatusColor(status)} 
          text={getStatusText(status, t)}
          style={{ whiteSpace: 'nowrap' }}
        />
      ),
    },
    {
      title: t('memo'),
      dataIndex: 'memo',
      key: 'memo',
      width: 150,
      render: (memo) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {memo || 'N/A'}
        </span>
      ),
    },
    {
      title: t('actions'),
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space>
          {record.confirmation_status === 'confirmed' ? (
            <>
              <Tooltip title={t('viewReceipt')}>
                <Button
                  type="primary"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => {
                    setSelectedRegistration(record);
                    setShowReceiptModal(true);
                  }}
                />
              </Tooltip>
              <Tooltip title={t('refund')}>
                <Button
                  danger
                  size="small"
                  icon={<SwapOutlined />}
                  onClick={() => {
                    setSelectedRegistration(record);
                    setShowRefundModal(true);
                  }}
                />
              </Tooltip>
            </>
          ) : record.confirmation_status === 'refund' ? (
            <Tooltip title={t('viewReceipt')}>
              <Button
                danger
                size="small"
                icon={<EyeOutlined />}
                onClick={() => {
                  setSelectedRegistration(record);
                  setShowReceiptModal(true);
                }}
              />
            </Tooltip>
          ) : (
            <Tooltip title={t('confirm')}>
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => handleConfirmClick(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ 
      marginTop: '75px', 
      marginLeft: '80px', 
      marginRight: '10px',
      background: '#f0f2f5', 
      minHeight: '100vh' 
    }}>
      <Sidebar />
      <Header />
      
      <Card>

        {/* Filters */}
        <Card 
          title={
            <Space>
              {t('confirm_registration')}
            </Space>
          }
          size="small" 
          style={{ marginBottom: '16px' }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                {t('Date Range')}:
              </label>
              <RangePicker
                value={filters.dateRange}
                onChange={(dates) => handleFilterChange('dateRange', dates)}
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                {t('paymentMethod')}:
              </label>
              <Select
                value={filters.paymentMethod}
                onChange={(value) => handleFilterChange('paymentMethod', value)}
                style={{ width: '100%' }}
                placeholder={t('all')}
                allowClear
              >
                {paymentMethods.map(method => (
                  <Option key={method} value={method}>{method}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                {t('status')}:
              </label>
              <Select
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                style={{ width: '100%' }}
                placeholder={t('all')}
                allowClear
              >
                <Option value="unconfirmed">{t('pending')}</Option>
                <Option value="confirmed">{t('confirmed')}</Option>
                <Option value="refund">{t('refunded')}</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                {t('studentName')}:
              </label>
              <Input
                value={filters.studentName}
                onChange={(e) => handleFilterChange('studentName', e.target.value)}
                placeholder={t('searchByName')}
                prefix={<SearchOutlined />}
                allowClear
              />
            </Col>
          </Row>
          <Row style={{ marginTop: '16px' }}>
            <Col>
              <Space>
                <Button 
                  icon={<ClearOutlined />} 
                  onClick={clearFilters}
                >
                  {t('Clear Filters')}
                </Button>
                <CSVLink 
                  data={csvData} 
                  filename={`registrations-${dayjs().format('YYYY-MM-DD')}.csv`}
                >
                  <Button 
                    type="primary" 
                    icon={<DownloadOutlined />}
                  >
                    {t('exportCSV')}
                  </Button>
                </CSVLink>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Data Table */}
        <div style={{ overflowX: 'auto' }}>
<style>
          {`
            .ant-table-tbody > tr:hover > td {
              background-color: #ffebee !important;
              cursor: pointer;
            }
          `}
        </style>
<Table
  columns={columns}
  dataSource={filteredRegistrations}
  loading={loading}
  rowClassName={(record) => 
    record.confirmation_status === 'refund' ? 'refunded-row' : ''
  }
  onRow={(record) => ({
    style: {
      backgroundColor: record.confirmation_status === 'refund' ? '#fff2f0' : 'transparent',
    }
  })}
  pagination={{
    ...pagination,
    onChange: (page, pageSize) => {
      setPagination(prev => ({ ...prev, current: page, pageSize }));
    },
  }}
  scroll={{ x: 'max-content' }}
  size="small"
  summary={(pageData) => {
    const pageTotals = pageData.reduce(
      (totals, registration) => {
        totals.price += parseFloat(registration.price) || 0;
        totals.inventory_price += parseFloat(registration.inventory_price) || 0;
        totals.discount += parseFloat(registration.discount) || 0;
        totals.vat += parseFloat(registration.vat) || 0;
        totals.finalPrice += parseFloat(registration.final_price) || 0;
        return totals;
      },
      { price: 0, inventory_price: 0, discount: 0, vat: 0, finalPrice: 0 }
    );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
          <Table.Summary.Cell index={0} colSpan={4}>

          </Table.Summary.Cell>
          <Table.Summary.Cell index={4} align="right">
            <strong style={{ whiteSpace: 'nowrap' }}>
              {formatNumber(pageTotals.price, i18n.language)}
            </strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5} align="right">
            <strong style={{ whiteSpace: 'nowrap' }}>
              {formatNumber(pageTotals.inventory_price, i18n.language)}
            </strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={6} align="right">
            <strong style={{ whiteSpace: 'nowrap' }}>
              {formatNumber(pageTotals.discount, i18n.language)}
            </strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={7} align="right">
            <strong style={{ whiteSpace: 'nowrap' }}>
              {formatNumber(pageTotals.vat, i18n.language)}
            </strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={8} align="right">
            <strong style={{ color: '#1890ff', whiteSpace: 'nowrap' }}>
              {formatNumber(pageTotals.finalPrice, i18n.language)}
            </strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={9} colSpan={4} />
        </Table.Summary.Row>
      </Table.Summary>
    );
  }}
/>
        </div>
      </Card>

      {/* Modals */}
      {selectedRegistration && (
        <>
          <InvoiceModal
            show={showInvoiceModal}
            onClose={() => setShowInvoiceModal(false)}
            registration={selectedRegistration}
          />
          <ReceiptModal
            show={showReceiptModal}
            onClose={() => setShowReceiptModal(false)}
            registration={selectedRegistration}
          />
          <RefundModal
            show={showRefundModal}
            onHide={() => setShowRefundModal(false)}
            registration={selectedRegistration}
            onRefund={fetchRegistrations}
          />
        </>
      )}
    </div>
  );
};

export default withRoleAccess(Accounting, ['admin', 'superadmin']);